// lib/client-log.ts
// Client-side structured logger with in-memory ring buffer + localStorage persistence.
// Mirrors the server-side dbg API: clog.info / warn / error / debug
//
// Usage (in any client component):
//   import { clog } from "@/lib/client-log";
//   clog.info("vote", "user tapped vote button", { nameId });
//   clog.error("bet", "placeMatchBet failed", { error: err.message });
//
// Logs are kept in memory (last 500 entries) and mirrored to localStorage
// under the key "bnc_client_log" so they survive page refreshes.
//
// The submitDiagnostics() export bundles all recent logs plus browser/device
// context into a payload for POST /api/diagnostics.

"use client";

import { getKnownBuildId } from "./known-build";

export type ClientLogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG";

export interface ClientLogEntry {
  ts: string;
  level: ClientLogLevel;
  cat: string;
  msg: string;
  data?: unknown;
  url?: string;       // page URL at time of log
  // monotonicMs (performance.now()) + visibility at write time — added for the
  // iOS WebKit JS-freeze investigation. wall-clock `ts` can jump backward/forward
  // (device sleep, clock sync) or simply be absent if the entry sat in a frozen
  // page's memory for a long time before a delayed flush finally sent it;
  // performance.now() is monotonic and unaffected by wall-clock changes, so
  // comparing monotonicMs deltas between entries (or against receipt time
  // server-side) can reveal a real stall that ts alone would hide.
  monotonicMs?: number;
  visibility?: DocumentVisibilityState;
}

// ── Ring buffer ───────────────────────────────────────────────────────────────

const MAX_ENTRIES = 500;
const STORAGE_KEY = "bnc_client_log";
const HWM_STORAGE_KEY = "bnc_client_log_hwm";

let buffer: ClientLogEntry[] = [];
let _initialized = false;

function loadFromStorage(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ClientLogEntry[];
      if (Array.isArray(parsed)) {
        // Keep only entries from the last 24h to avoid stale data
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        buffer = parsed.filter((e) => new Date(e.ts).getTime() > cutoff).slice(-MAX_ENTRIES);
      }
    }
  } catch {
    // Corrupted storage — start fresh
    buffer = [];
  }
}

function persistToStorage(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer.slice(-MAX_ENTRIES)));
  } catch {
    // Storage full or unavailable — not critical
  }
}

// The flush high-water mark must survive a page reload, same as `buffer` above —
// otherwise every fresh page load resets it to 0 and resends the entire
// persisted (up to 24h / 500-entry) backlog from scratch, even entries the
// server already received hours or days ago. Confirmed in the field: the same
// clientTs entry received 5 separate times over a 13-hour span from one
// device, each with a larger staleByMs — pure duplicate resend, not repeated
// real staleness. This also pollutes the iOS-freeze investigation's own
// signal (PROJECT.md already warns to cross-check bursts against server
// restart history — a resend-on-reload burst is the same kind of confound).
function loadHwmFromStorage(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = localStorage.getItem(HWM_STORAGE_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function persistHwm(hwm: number): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(HWM_STORAGE_KEY, String(hwm));
  } catch {
    // Storage full or unavailable — not critical, worst case is a resend
  }
}

function ensureInit(): void {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;
  loadFromStorage();
  installGlobalHandlers();
}

// ── Core write ────────────────────────────────────────────────────────────────

function write(level: ClientLogLevel, cat: string, msg: string, data?: unknown): void {
  ensureInit();

  const entry: ClientLogEntry = {
    ts: new Date().toISOString(),
    level,
    cat,
    msg,
    ...(data !== undefined ? { data } : {}),
    url: typeof window !== "undefined" ? window.location.pathname : undefined,
    monotonicMs: typeof performance !== "undefined" ? performance.now() : undefined,
    visibility: typeof document !== "undefined" ? document.visibilityState : undefined,
  };

  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();
  persistToStorage();

  // If the user opted into diagnostics sharing, an error schedules a debounced
  // flush so admins get the error plus its surrounding context. No-op otherwise.
  if (level === "ERROR") scheduleErrorFlush();

  // Mirror to console with a consistent prefix so devtools show category
  const prefix = `[BNC/${cat}]`;
  if (level === "ERROR") console.error(prefix, msg, data ?? "");
  else if (level === "WARN") console.warn(prefix, msg, data ?? "");
  else if (level === "DEBUG") console.debug(prefix, msg, data ?? "");
  else console.log(prefix, msg, data ?? "");
}

// ── Global error handlers ─────────────────────────────────────────────────────

function installGlobalHandlers(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    write("ERROR", "global", event.message || "Uncaught error", {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack?.slice(0, 500),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    write("ERROR", "global", "Unhandled promise rejection", {
      reason: reason instanceof Error
        ? { message: reason.message, stack: reason.stack?.slice(0, 500) }
        : String(reason),
    });
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

export const clog = {
  info:  (cat: string, msg: string, data?: unknown) => write("INFO",  cat, msg, data),
  warn:  (cat: string, msg: string, data?: unknown) => write("WARN",  cat, msg, data),
  error: (cat: string, msg: string, data?: unknown) => write("ERROR", cat, msg, data),
  debug: (cat: string, msg: string, data?: unknown) => write("DEBUG", cat, msg, data),
};

// ── Diagnostics bundle export ─────────────────────────────────────────────────

export interface DiagnosticsBundle {
  clientLogs: ClientLogEntry[];
  browser: {
    userAgent: string;
    language: string;
    platform: string;
    screenSize: string;
    viewportSize: string;
    online: boolean;
    cookieEnabled: boolean;
    href: string;
    // True when running as an installed home-screen PWA (display-mode:
    // standalone, or iOS's pre-matchMedia navigator.standalone). A push
    // subscription endpoint alone (e.g. web.push.apple.com) only proves a
    // PWA was installed at some point — not that THIS session is running
    // in it rather than a regular browser tab. Added after exactly that
    // ambiguity blocked diagnosing an iOS-PWA-specific clog gap.
    standalone: boolean;
    // The build id this page was actually rendered with (lib/known-build.ts,
    // set by update-checker.tsx). Added after the June 17-19 "tap Update Now,
    // it just loops" bug — when troubleshooting a user's logs we had no way
    // to confirm whether they were even running the build the logs implied.
    // null only if update-checker.tsx hasn't mounted yet (shouldn't happen —
    // it's in the root layout — but diagnostics should never throw over it).
    buildId: string | null;
  };
  capturedAt: string;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function collectDiagnostics(): DiagnosticsBundle {
  ensureInit();
  return {
    clientLogs: [...buffer],
    browser: typeof window !== "undefined" ? {
      userAgent:    navigator.userAgent,
      language:     navigator.language,
      platform:     navigator.platform,
      screenSize:   `${screen.width}×${screen.height}`,
      viewportSize: `${window.innerWidth}×${window.innerHeight}`,
      online:       navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      href:         window.location.href,
      standalone:   isStandalone(),
      buildId:      getKnownBuildId(),
    } : {
      userAgent: "unknown", language: "unknown", platform: "unknown",
      screenSize: "unknown", viewportSize: "unknown",
      online: true, cookieEnabled: true, href: "unknown", standalone: false,
      buildId: null,
    },
    capturedAt: new Date().toISOString(),
  };
}

export function clearClientLog(): void {
  buffer = [];
  if (typeof window !== "undefined") {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

// ── Opt-in auto-submit ────────────────────────────────────────────────────────
// When the user has enabled "share diagnostics", the client periodically flushes
// its log buffer to /api/diagnostics so admins can troubleshoot client issues
// without the user manually tapping "Submit Diagnostics". Only active after
// initDiagnosticsAutoFlush(true) is called (from DiagnosticsAutoFlush, which is
// gated on the user's shareDiagnostics preference). Default OFF.
//
// Flush triggers:
//   • periodic   — every FLUSH_INTERVAL_MS, only if there are new entries
//   • on error   — debounced shortly after any ERROR-level entry
//   • on hide    — when the page is hidden/unloaded (sendBeacon, best-effort)
// We send a COPY and only clear up to the high-water mark on success, so logs
// added during an in-flight request aren't lost.

const FLUSH_INTERVAL_MS = 20_000;   // periodic flush cadence
const ERROR_FLUSH_DEBOUNCE_MS = 4_000; // wait a beat after an error to batch context

let _autoFlushEnabled = false;
let _autoFlushInstalled = false;
let _lastFlushedTs = 0;             // ts (ms) of the newest entry already sent
let _flushInFlight = false;
let _periodicTimer: ReturnType<typeof setInterval> | null = null;
let _errorFlushTimer: ReturnType<typeof setTimeout> | null = null;

function entriesSince(sinceMs: number): ClientLogEntry[] {
  return buffer.filter((e) => new Date(e.ts).getTime() > sinceMs);
}

// Safari (incl. standalone PWA) enforces a 64KB ceiling on TOTAL queued data
// across all in-flight sendBeacon/keepalive-fetch requests for the page —
// not per-request. A session that accumulates a large backlog (e.g. a stale
// localStorage buffer from before this fix existed, or just heavy SSE/vote
// activity) can produce a single flush payload that alone exceeds it, or
// pushes a shared queue already near the limit over the edge. Once that
// happens sendBeacon returns false / throws — silently, with no network
// request ever sent — and the old code never advanced past it, so EVERY
// subsequent flush attempt retried the same (or larger) backlog and failed
// the same way forever. Confirmed in the field via Safari's remote
// inspector: "Reached maximum amount of queued data of 64Kb for keepalive
// requests" on every flushDiagnostics call, for a session that had
// genuinely zero successful diagnostics submissions despite voting
// normally all day.
//
// Fix: cap each flush to the most recent MAX_FLUSH_ENTRIES entries (well
// under the limit even with verbose `data` payloads), and always advance
// the watermark to what we attempted — a dropped/oversized batch is lost
// telemetry, which is fine, vs. the old behavior of never recovering at all.
const MAX_FLUSH_ENTRIES = 100;

async function flushDiagnostics(reason: string, useBeacon = false): Promise<void> {
  if (!_autoFlushEnabled || typeof window === "undefined") return;
  if (_flushInFlight && !useBeacon) return;

  const pending = entriesSince(_lastFlushedTs).slice(-MAX_FLUSH_ENTRIES);
  if (pending.length === 0) return;

  // Snapshot the high-water mark BEFORE sending so concurrent writes aren't dropped.
  const hwm = new Date(pending[pending.length - 1].ts).getTime();
  const bundle: DiagnosticsBundle & { reason?: string } = {
    ...collectDiagnostics(),
    clientLogs: pending,
    reason,
  };

  // Page-hide path: sendBeacon survives unload where fetch may be cancelled.
  if (useBeacon && typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    try {
      const blob = new Blob([JSON.stringify(bundle)], { type: "application/json" });
      const ok = navigator.sendBeacon("/api/diagnostics", blob);
      // Advance the watermark even on failure (queue full, etc.) — retrying
      // the identical or growing backlog forever is the bug we're fixing.
      // A skipped batch is acceptable lost telemetry; a permanently stuck
      // flusher is not.
      if (!ok) console.warn("[BNC/clog] sendBeacon rejected (queue full?) — dropping batch", { reason, count: pending.length });
      _lastFlushedTs = hwm;
      persistHwm(hwm);
    } catch (err) {
      console.warn("[BNC/clog] sendBeacon threw — dropping batch", { reason, count: pending.length, error: String(err) });
      _lastFlushedTs = hwm;
      persistHwm(hwm);
    }
    return;
  }

  _flushInFlight = true;
  try {
    const res = await fetch("/api/diagnostics", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bundle),
      // No keepalive here: this path only runs while the page is alive
      // (periodic/error/foreground triggers), so it doesn't need to survive
      // unload — and keepalive would share the same 64KB queue ceiling as
      // sendBeacon above, for no benefit on this path.
    });
    if (res.ok) { _lastFlushedTs = hwm; persistHwm(hwm); }
    else console.warn("[BNC/clog] diagnostics POST non-OK — will retry next flush", { reason, status: res.status });
  } catch (err) {
    // Network blip — keep the entries; the next flush retries from _lastFlushedTs.
    console.warn("[BNC/clog] diagnostics POST threw — will retry next flush", { reason, error: String(err) });
  } finally {
    _flushInFlight = false;
  }
}

export function initDiagnosticsAutoFlush(enabled: boolean): void {
  if (typeof window === "undefined") return;
  _autoFlushEnabled = enabled;
  if (!enabled || _autoFlushInstalled) return;
  _autoFlushInstalled = true;
  ensureInit();

  // Resume from wherever the last successful flush (possibly in a prior page
  // load) left off, instead of resetting to 0 — see loadHwmFromStorage() for
  // why. 0 here just means "nothing has ever been flushed on this device",
  // in which case sending the current buffer once is correct.
  _lastFlushedTs = loadHwmFromStorage();

  _periodicTimer = setInterval(() => { flushDiagnostics("periodic"); }, FLUSH_INTERVAL_MS);

  // Page hidden / unloading — best-effort beacon flush. iOS Safari (especially
  // installed PWAs) suspends JS execution almost immediately on backgrounding,
  // so the periodic timer and even sendBeacon on pagehide are unreliable there —
  // also flush on the *return* transition (visible again), which iOS fires
  // reliably when the app is reopened, so logs from the prior session escape
  // even if nothing flushed while backgrounded.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushDiagnostics("page-hidden", true);
    else flushDiagnostics("page-visible");
  });
  window.addEventListener("pagehide", () => flushDiagnostics("pagehide", true));
}

// Exposed for call sites that want to flush right after a meaningful action
// (e.g. casting a vote) instead of waiting for a timer — belt-and-suspenders
// on iOS PWAs where background timers/beacons are unreliable.
export function flushDiagnosticsNow(reason: string): void {
  flushDiagnostics(reason);
}

// Awaitable variant for call sites that need the flush to actually finish
// before doing something else (e.g. a hard reload — the page being torn
// down mid-flush would lose whatever hadn't gone out yet).
export async function flushDiagnosticsAndWait(reason: string): Promise<void> {
  await flushDiagnostics(reason);
}

// Called from write() when an ERROR is logged and auto-flush is on.
function scheduleErrorFlush(): void {
  if (!_autoFlushEnabled) return;
  if (_errorFlushTimer) clearTimeout(_errorFlushTimer);
  _errorFlushTimer = setTimeout(() => { flushDiagnostics("error"); }, ERROR_FLUSH_DEBOUNCE_MS);
}
